import {Directive, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {ColDef, GridApi, GridReadyEvent} from "ag-grid-community";
import {IPlayer} from "../../interfaces/player/IPlayer";
import {PlayersService} from "../../services/players.service";
import {PhoneFormatPipe} from "../../pipes/phone-format.pipe";
import {Router} from "@angular/router";
import {ErrorHandlingService} from "../../services/error-handling.service";
import {IPlayerSearchParams} from "../../interfaces/player/IPlayerSearchParams";
import {UIStateEnum} from "../../enum/UIStateEnum";

@Directive()
export abstract class BasePlayerSearchComponent implements OnInit {
  public uiState: UIStateEnum = UIStateEnum.ShowLoading;
  public uiStateEnumForTemplate = UIStateEnum;
  // a partial phone number matches too many players, so only a full 10-digit number is searchable
  private fullPhoneNumberRegex = /^\d{3}-\d{3}-\d{4}$/;
  public phoneNumberControl: FormControl<string | null> = new FormControl<string | null>('', Validators.pattern(this.fullPhoneNumberRegex));
  private guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  public playerIdControl: FormControl<string | null> = new FormControl('', Validators.pattern(this.guidRegex));

  public playerSearchForm = new FormGroup({
    firstName: new FormControl(''),
    lastName: new FormControl(''),
    email: new FormControl(''),
    phoneNumber: this.phoneNumberControl,
    playerId: this.playerIdControl
  });

  public players: IPlayer[] = [];
  public columnDefs: ColDef[] = [
    {headerName: 'Id', field: 'Id', resizable: true, sortable: true},
    {headerName: 'First Name', field: 'FirstName', resizable: true, sortable: true},
    {headerName: 'Last Name', field: 'LastName', resizable: true, sortable: true},
    {headerName: 'Email', field: 'Email', resizable: true, sortable: true},
    {
      headerName: 'Phone',
      field: 'Phone',
      valueFormatter: params => {
        if (params.value && params.value.includes('***')) {
          return params.value;
        }
        return this.phoneFormatPipe.transform(params.value);
      },
      resizable: true,
      sortable: true
    },
  ];

  private gridApi!: GridApi<IPlayer>;

  protected constructor(
    protected playerService: PlayersService,
    protected phoneFormatPipe: PhoneFormatPipe,
    protected errorHandlingService: ErrorHandlingService,
    protected router: Router
  ) {
    this.phoneNumberControl.valueChanges.subscribe((value: string | null) => {
      if (value) {
        const formattedValue = this.phoneFormatPipe.transform(value);
        this.phoneNumberControl.setValue(formattedValue, {emitEvent: false});
      }
    });
  }

  ngOnInit(): void {
    this.retrieveUsersWithSearchParams(this.getSearchParams());
  }

  private retrieveUsersWithSearchParams(paramsP?: IPlayerSearchParams) {
    this.uiState = UIStateEnum.ShowLoading;

    this.playerService.getPlayersBySearchParams(paramsP, this.isGlobalSearch()).subscribe({
      next: res => {
        this.players = res;
        this.uiState = UIStateEnum.ShowData;
      },
      error: error => {
        this.uiState = UIStateEnum.ShowData;
        this.errorHandlingService.displayPageLevelErrorMessage(error);
      }
    });
  }

  onResetClick() {
    this.playerSearchForm.reset();
    this.retrieveUsersWithSearchParams(this.getSearchParams());
  }

  onFormSubmit() {
    if (!this.playerSearchForm.valid) {
      this.playerSearchForm.markAllAsTouched();
      return;
    }

    this.retrieveUsersWithSearchParams(this.getSearchParams());
  }

  onGridReady(params: GridReadyEvent<IPlayer>) {
    this.gridApi = params.api;
    this.gridApi.sizeColumnsToFit({defaultMinWidth: 100});
  }

  async onRowSelected(chosenPlayerP: any) {
    let player: IPlayer = chosenPlayerP.data;
    await this.router.navigateByUrl(this.getPlayerManagementUrl(player.Id));
  }

  protected abstract getSearchParams(): IPlayerSearchParams | undefined;

  protected abstract isGlobalSearch(): boolean;

  protected abstract getPlayerManagementUrl(playerId: string): string;
}
